<script setup lang="ts">
import { computed, watch } from 'vue';
import moment from 'moment';
import type { OutcomeVoucherOption, OutcomeVoucherRow } from '../../../interfaces';
import type { FieldSchema } from '../../../types';
import { getShelterNow } from '@Constants';

const props = withDefaults(
  defineProps<{
    disabled?: boolean;
    error?: string;
    field?: FieldSchema;
    placeholder?: string;
  }>(),
  {
    placeholder: 'components.formFields.outcomeVouchersPlaceholder',
  }
);

const model = defineModel<OutcomeVoucherRow[]>({ default: [] });

const options = computed<OutcomeVoucherOption[]>(() => props.field?.edit?.options || []);
const outcomeDate = computed<Date | undefined>(() => props.field?.settings?.outcomeDate);
const preselectedIds = computed<string[]>(() => props.field?.settings?.preselectedIds || []);
const rows = computed<OutcomeVoucherRow[]>(() => (Array.isArray(model.value) ? model.value : []));

const selectedIds = computed({
  get: () => rows.value.map(({ voucher_template_id }) => voucher_template_id),
  set: (ids: string[]) => onSelectionUpdate(ids),
});

// NOTE :: Days from the outcome date to each row's expiration date, shown beside the picker so
// staff can check the math. Both are shelter wall-clock dates, so a plain day diff is right.
const validDaysById = computed<Record<string, number>>(() => {
  const outcomeDay = moment(outcomeDate.value || getShelterNow()).startOf('day');

  return rows.value.reduce((validDays: Record<string, number>, row) => {
    if (row.no_expiration || !row.exp_date) return validDays;

    validDays[row.voucher_template_id] = moment(row.exp_date).startOf('day').diff(outcomeDay, 'days');

    return validDays;
  }, {});
});

watch(outcomeDate, onOutcomeDateUpdate);
watch(preselectedIds, onPreselectedIdsUpdate, { immediate: true });

// HELPERS
// NOTE :: Wall-clock math on purpose: the outcome date is already the shelter's wall clock
// (getShelterNow), so adding days to it gives the shelter's date with no timezone shift.
// NOTE :: A voucher always has a date unless "No expiration" is ticked. Without "Valid for (days)"
// on the voucher type, it starts at today on the shelter's clock.
function getDefaultExpDate(validDays: number | null): Date {
  if (!validDays) return moment(getShelterNow()).startOf('day').toDate();

  return moment(outcomeDate.value || getShelterNow())
    .startOf('day')
    .add(validDays, 'days')
    .toDate();
}

function buildRow(option: OutcomeVoucherOption): OutcomeVoucherRow {
  const validDays = option.valid_days ?? null;

  return {
    exp_date: getDefaultExpDate(validDays),
    no_expiration: false,
    title: option.title,
    touched: false,
    valid_days: validDays,
    voucher_template_id: option._id,
  };
}

function isSameExpDate(currentDate: Date | null, nextDate: Date | null) {
  if (!currentDate || !nextDate) return currentDate === nextDate;

  return moment(currentDate).isSame(nextDate, 'day');
}

function updateRow(voucherTemplateId: string, changes: Partial<OutcomeVoucherRow>) {
  model.value = rows.value.map((row) =>
    row.voucher_template_id === voucherTemplateId ? { ...row, ...changes, touched: true } : row
  );
}

// EVENTS
function onSelectionUpdate(ids: string[]) {
  const keptRows = rows.value.filter(({ voucher_template_id }) => ids.includes(voucher_template_id));
  const keptIds = keptRows.map(({ voucher_template_id }) => voucher_template_id);

  const addedRows = options.value
    .filter(({ _id }) => ids.includes(_id) && !keptIds.includes(_id))
    .map((option) => buildRow(option));

  model.value = [...keptRows, ...addedRows];
}

// NOTE :: The animal profile's picks pre-fill a line that has no vouchers yet. Rows already on
// the cart line are never replaced.
function onPreselectedIdsUpdate() {
  if (rows.value.length || !preselectedIds.value.length) return;

  onSelectionUpdate(preselectedIds.value);
}

function onOutcomeDateUpdate() {
  model.value = rows.value.map((row) => {
    const followsOutcomeDate = !row.touched && !row.no_expiration && !!row.valid_days;
    if (!followsOutcomeDate) return row;

    return { ...row, exp_date: getDefaultExpDate(row.valid_days) };
  });
}

// NOTE :: The picker echoes its value back when it mounts. Every row mounts at once, and each
// update rebuilds the list from the same snapshot, so echoes would overwrite each other and mark
// rows as touched. Only a real change of day counts.
function onExpDateUpdate(voucherTemplateId: string, expDate: Date | Date[] | null) {
  const singleDate = Array.isArray(expDate) ? (expDate[0] ?? null) : expDate;
  const row = rows.value.find(({ voucher_template_id }) => voucher_template_id === voucherTemplateId);
  const isSameDay = isSameExpDate(row?.exp_date ?? null, singleDate);

  if (isSameDay) return;

  updateRow(voucherTemplateId, { exp_date: singleDate });
}

function onNoExpirationUpdate(voucherTemplateId: string, noExpiration: boolean) {
  const row = rows.value.find(({ voucher_template_id }) => voucher_template_id === voucherTemplateId);
  const CHANGES = noExpiration
    ? { exp_date: null, no_expiration: true }
    : { exp_date: getDefaultExpDate(row?.valid_days ?? null), no_expiration: false };

  updateRow(voucherTemplateId, CHANGES);
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <AFieldMultiSelect
      v-model="selectedIds"
      optionLabel="title"
      optionValue="_id"
      :error
      :options
      :placeholder
    />

    <div
      v-for="row in rows"
      class="flex flex-wrap items-center gap-3 rounded-md bg-gray-50 p-2 text-sm"
      :key="row.voucher_template_id"
    >
      <p class="min-w-32 flex-1 truncate font-semibold text-gray-700">{{ row.title }}</p>

      <div class="flex items-center gap-2">
        <span class="text-gray-700">{{ $t('components.formFields.outcomeVouchersExpires') }}</span>

        <MFieldDatePicker
          class="w-44"
          :disabled="disabled || row.no_expiration"
          :error="!row.no_expiration && !row.exp_date ? error : undefined"
          :minDate="outcomeDate"
          :modelValue="row.exp_date || undefined"
          @update:modelValue="(expDate: Date | Date[] | null) => onExpDateUpdate(row.voucher_template_id, expDate)"
        />
      </div>

      <span
        v-if="validDaysById[row.voucher_template_id] !== undefined"
        class="text-xs text-gray-500"
      >
        {{
          $t(
            'components.formFields.outcomeVouchersValidFor',
            { count: validDaysById[row.voucher_template_id] },
            validDaysById[row.voucher_template_id]
          )
        }}
      </span>

      <div class="flex items-center gap-2">
        <Checkbox
          binary
          :disabled
          :inputId="`no-expiration-${row.voucher_template_id}`"
          :modelValue="row.no_expiration"
          @update:modelValue="(noExpiration: boolean) => onNoExpirationUpdate(row.voucher_template_id, noExpiration)"
        />

        <label
          class="text-gray-700"
          :for="`no-expiration-${row.voucher_template_id}`"
        >
          {{ $t('components.formFields.outcomeVouchersNoExpiration') }}
        </label>
      </div>
    </div>
  </div>
</template>
